export const ABOUT_TITLE = "About XpressPrompts Universal Short Prompt Commands Dictionary";
export const ABOUT_TAGLINE = "Making AI easier to use, one short command at a time.";

export const ABOUT_INTRO = [
  "Universal Short-Command Prompt Dictionary™ (AI Shorthand) is a searchable directory of simple, ready-to-use prompting shortcuts designed for ChatGPT, Claude, Gemini and other conversational AI tools.",
  "Our purpose is simple: to communicate and make AI more accessible, understandable and useful for everyone with short clear concise command prompts.",
  "You don't need to become a prompt engineer to benefit from artificial intelligence. You simply need to know what to ask AI to do — and how to ask it clearly.",
  "That's where AI Shorthand comes in.",
  "Instead of spending time writing complicated prompts or trying to find the right words, you can search for the task you want to accomplish, find a relevant short command, personalise it with a few details and use it directly in your preferred AI tool.",
];

export const ABOUT_SECTIONS = [
  {
    id: "what-is-ai-shorthand",
    title: "What Is AI Shorthand?",
    paragraphs: [
      "AI Shorthand is a practical prompt dictionary that helps turn everyday tasks into clear, actionable AI instructions.",
      "Think of it as a shortcut between knowing what you want and knowing how to ask AI for it.",
      "Whether you're writing, researching, learning, brainstorming, planning, analysing information or solving everyday problems, AI Shorthand helps you find a useful starting point without having to construct a detailed prompt from scratch.",
      "Our commands are designed to be straightforward, reusable and easy to adapt to your individual needs.",
    ],
  },
  {
    id: "how-it-works",
    title: "How Does It Work?",
    video: true,
    paragraphs: ["Using AI Shorthand is simple:"],
    steps: [
      { label: "Search", text: "Enter a task, keyword or topic, or explore commands by category." },
      { label: "Choose", text: "Find a short command that matches what you want AI to do." },
      { label: "Copy", text: "Copy the command into your preferred AI tool." },
      { label: "Customise", text: "Add any relevant information, context or instructions." },
      { label: "Create", text: "Let AI respond, then refine the results through conversation." },
    ],
    closing: "No complicated setup. No specialist terminology. No prompt-engineering experience required.",
  },
  {
    id: "who-it-is-for",
    title: "Who Is AI Shorthand For?",
    paragraphs: ["AI Shorthand is designed for anyone who wants to make better practical use of AI, including:"],
    bullets: [
      "Beginners discovering what AI can help them accomplish.",
      "Professionals looking to simplify everyday tasks and workflows.",
      "Business owners and entrepreneurs exploring ways to improve productivity.",
      "Students and educators using AI to support learning and understanding.",
      "Content creators and writers developing ideas, drafts and creative projects.",
      "Everyday users who simply want a quicker, easier way to communicate with AI.",
    ],
    closing: "Whether you use AI occasionally or every day, AI Shorthand gives you a searchable collection of instructions to explore and adapt.",
  },
  {
    id: "why-short-commands",
    title: "Why Short Commands?",
    paragraphs: [
      "AI can assist with an enormous range of tasks, but knowing how to communicate what you want isn't always obvious.",
      "A short command gives you a clear starting point.",
      "Rather than focusing on the technicalities of writing elaborate prompts, AI Shorthand encourages a simpler approach: focus on the task, find the command, and add the details that matter.",
      "The result is a more approachable way to discover AI's possibilities, build confidence and spend less time wondering what to type.",
    ],
  },
  {
    id: "multiple-ai-tools",
    title: "One Dictionary. Multiple AI Tools.",
    paragraphs: [
      "AI Shorthand is designed around plain-language instructions that can be used with leading conversational AI platforms, including ChatGPT, Claude and Gemini.",
      "An important distinction: AI Shorthand commands are user-created prompting shortcuts. They are not official built-in slash commands, special functions or features supplied by OpenAI, Anthropic or Google.",
      "Each command works as an instruction that you give to your chosen AI assistant. Responses and capabilities may vary depending on the AI platform, model and context provided.",
    ],
  },
  {
    id: "our-vision",
    title: "Our Vision",
    paragraphs: [
      "We believe useful AI skills should not be limited to people with technical knowledge.",
      "As AI becomes part of everyday life, the ability to communicate clearly with it should feel natural, not complicated.",
      "AI Shorthand aims to make that possible by organising practical prompting instructions into one searchable, easy-to-use resource.",
      "Less time figuring out how to ask. More time getting things done.",
    ],
  },
  {
    id: "discover-ai",
    title: "Discover how XpressPrompts Can help you Do",
    paragraphs: [
      "Search the dictionary, explore a category, choose a command and start putting AI to work.",
      "AI Shorthand — Know what to ask. Find the command. Make AI work for you.",
    ],
    dictionaryLink: true,
  },
];

export const ABOUT_DEVELOPER = {
  heading: "Diane Shawe MEd — Founder and Developer of XpressPrompts",
  paragraphs: [
    "Diane Shawe is an entrepreneur, author and business innovator with over 35 years of experience in business development, customer engagement and service innovation. Her expertise spans artificial intelligence, behavioural economics, entrepreneurship and digital transformation. She holds a Bachelor's degree in Business Administration, a Master of Education (MEd) and a HarvardX certificate in Entrepreneurship in Emerging Economies.",
    "Diane has supported more than 300 business startups across over 50 countries.",
  ],
  mission: "She created XpressPrompts, a universal short-command prompt dictionary designed to make AI more accessible, practical and easier to use. Her vision is to enable individuals, institutions and enterprises to achieve AI readiness and utilise artificial intelligence at scale. XpressPrompts simplifies how people communicate with AI, helping users unlock its potential without needing specialist technical knowledge.",
};